import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useCentralStore } from "../../../store/Central";
import { showNotification } from "../../../store/Central/selectors";
import { PageTitle } from "@/components/src/page-title/page-title.component";
import { SupabaseViewEnum } from "@/lib/supabase/supabaseTypes";
import { HeaderPortal } from "@/components/src/header-portal/header-portal.component";
import { Database, Shield } from "lucide-react";
import { MigrateAmountsTable } from "./migrate-amounts-table.component";
import { CompletedJobDocumentType } from "@/lib/supabase/supabaseTypes";

export const MigrateAmountsPage = () => {
	const { organizationId, profile } = useCentralStore((state) => ({
		organizationId: state.organization?.id,
		profile: state.profile,
	}));
	const [loading, setLoading] = useState<boolean>(false);
	const [jobDocuments, setJobDocuments] = useState<
		CompletedJobDocumentType[]
	>([]);
	const [jobsWithMissingOrgSettings, setJobsWithMissingOrgSettings] =
		useState<CompletedJobDocumentType[]>([]);

	// Check if user should have access to support tools
	const isSupportAccount =
		profile?.email?.includes("@anthropic.com") ||
		profile?.email?.includes("@dentdesk.ch") ||
		import.meta.env.MODE === "development" ||
		window.location.hostname === "localhost";

	useEffect(() => {
		if (isSupportAccount) {
			fetchData();
		}
	}, [isSupportAccount]);

	const fetchData = async () => {
		if (!organizationId) {
			showNotification({
				message: "Fehler beim Laden der Daten: Keine Organisation",
				type: "error",
			});
			return;
		}

		setLoading(true);

		const { data, error } = await supabase
			.from(SupabaseViewEnum.COMPLETED_JOB_DOCUMENTS_VIEW)
			.select();

		if (error) {
			showNotification({
				message: "Fehler beim Laden der Daten: " + error.message,
				type: "error",
			});
			setLoading(false);
			return;
		}

		const noAmount = data.filter(
			(item: CompletedJobDocumentType) => !item.amount
		);
		setJobDocuments(noAmount);

		const missingOrgSettings = data.filter(
			(item: CompletedJobDocumentType) =>
				item.job_mwst_number === null ||
				item.job_is_mwst_eust === null ||
				item.job_round_to === null
		);
		setJobsWithMissingOrgSettings(missingOrgSettings);

		setLoading(false);
	};

	if (!isSupportAccount) {
		return (
			<div className="flex flex-col px-12 py-6">
				<HeaderPortal>
					<PageTitle
						title={[
							{ name: "Support", link: "/support" },
							{ name: "Zugriff verweigert" },
						]}
						icon={
							<Shield
								width="16px"
								height="16px"
								color="rgb(55, 65, 81)"
							/>
						}
					/>
				</HeaderPortal>
				<div className="flex flex-col items-center justify-center py-12">
					<Shield className="w-16 h-16 text-red-500 mb-4" />
					<h2 className="text-xl font-semibold text-gray-900 mb-2">
						Zugriff verweigert
					</h2>
					<p className="text-gray-600">
						Sie haben keine Berechtigung für diese Support-Tools.
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="flex flex-col px-12 py-6">
			<HeaderPortal>
				<PageTitle
					title={[
						{ name: "Support", link: "/support" },
						{ name: "Beträge Migrieren" },
					]}
					icon={
						<Database
							width="16px"
							height="16px"
							color="rgb(55, 65, 81)"
						/>
					}
				/>
			</HeaderPortal>

			<div className="flex flex-col gap-6 py-4">
				{loading && (
					<div className="flex justify-center py-8">
						<div>Lädt...</div>
					</div>
				)}

				{!loading && (
					<>
						<div className="flex flex-col gap-2">
							<h2 className="text-lg font-semibold">
								1. Fehlende Beträge aktualisieren
							</h2>
							<p className="text-sm text-gray-600">
								Abgeschlossene Lieferscheine mit fehlenden
								Beträgen
							</p>
							<p className="text-sm font-medium">
								{jobDocuments.length} Lieferscheine mit
								fehlenden Beträgen
							</p>
						</div>

						{jobDocuments.length > 0 ? (
							<MigrateAmountsTable
								jobDocuments={jobDocuments}
								jobsWithMissingOrgSettings={[]}
								onRefresh={fetchData}
								mode="amounts"
							/>
						) : (
							<div className="text-center py-4 text-gray-500 border rounded-md">
								Keine Lieferscheine mit fehlenden Beträgen
								gefunden.
							</div>
						)}

						<hr className="my-4" />

						<div className="flex flex-col gap-2">
							<h2 className="text-lg font-semibold">
								2. Fehlende Organisationseinstellungen
								aktualisieren
							</h2>
							<p className="text-sm text-gray-600">
								Aufträge mit fehlender MwSt.-Nummer,
								EUSt.-Angabe oder Rundungseinstellung
							</p>
							<p className="text-sm font-medium">
								{jobsWithMissingOrgSettings.length} Aufträge mit
								fehlenden Einstellungen
							</p>
						</div>

						{jobsWithMissingOrgSettings.length > 0 ? (
							<MigrateAmountsTable
								jobDocuments={[]}
								jobsWithMissingOrgSettings={
									jobsWithMissingOrgSettings
								}
								onRefresh={fetchData}
								mode="orgSettings"
							/>
						) : (
							<div className="text-center py-4 text-gray-500 border rounded-md">
								Keine Aufträge mit fehlenden
								Organisationseinstellungen gefunden.
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
};
